/**
 * Print helpers that wait for the document (and images) before calling print(),
 * and that leave Firefox pdf.js viewers open instead of swallowing print errors.
 */


export type PrintHtmlResult =
  | { status: 'printed' }
  | { status: 'popup-blocked' };

export type PrintPdfResult =
  | { status: 'printed' }
  | { status: 'left-open' };

export type PrintHtmlOptions = {
  target?: string;
  features?: string;
};

/**
 * Resolves when the window's document is complete, or after `timeoutMs` at the
 * latest: a PDF viewer may never report 'complete' to the opener, and print
 * must not hang on it.
 */
function waitForDocumentComplete(win: Window, timeoutMs = 3000): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      try {
        win.document.removeEventListener('readystatechange', done);
      } catch {
        // cross-origin document: nothing to detach
      }
      win.removeEventListener('load', done);
      resolve();
    };
    const done = () => {
      try {
        if (win.document.readyState === 'complete') finish();
      } catch {
        finish();
      }
    };
    try {
      if (win.document.readyState === 'complete') {
        finish();
        return;
      }
      win.document.addEventListener('readystatechange', done);
    } catch {
      finish();
      return;
    }
    win.addEventListener('load', done);
    window.setTimeout(finish, timeoutMs);
  });
}

function waitForImages(win: Window): Promise<void> {
  const images = Array.from(win.document.images);
  if (images.length === 0) return Promise.resolve();

  return Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
}

export async function printHtmlWindow(
  html: string,
  options?: PrintHtmlOptions,
): Promise<PrintHtmlResult> {
  const target = options?.target ?? '_blank';
  const printWindow =
    options?.features !== undefined
      ? window.open('', target, options.features)
      : window.open('', target);

  if (!printWindow) {
    return { status: 'popup-blocked' };
  }

  printWindow.document.write(html);
  printWindow.document.close();
  await waitForDocumentComplete(printWindow);
  await waitForImages(printWindow);
  printWindow.focus();
  printWindow.print();
  return { status: 'printed' };
}

export function isFirefoxPdfJsViewer(win: Window): boolean {
  try {
    if ('PDFViewerApplication' in win) return true;
    const outer = win.document.getElementById('outerContainer');
    const viewer =
      win.document.getElementById('viewerContainer') ?? win.document.getElementById('viewer');
    return Boolean(outer && viewer);
  } catch {
    // Cross-origin PDF plugin: cannot inspect, treat as a viewer.
    return true;
  }
}

export async function tryPrintPdfWindow(popup: Window): Promise<PrintPdfResult> {
  await waitForDocumentComplete(popup);

  // The caller tells the user (common:print.pdfUseBrowserDialog); this module
  // stays free of i18n and stores so it can be called from plain functions.
  if (isFirefoxPdfJsViewer(popup)) {
    return { status: 'left-open' };
  }

  try {
    popup.focus();
    popup.print();
    return { status: 'printed' };
  } catch {
    return { status: 'left-open' };
  }
}
